import axios from 'axios'
import {FC, useEffect, useMemo, useState} from 'react'
import {useSearchParams} from 'react-router-dom'

type ReviewerAccessData = {
  reviewerId?: number
  reviewerName?: string | null
  reviewerEmail?: string | null
  applicationId?: number
  applicationTitle?: string | null
  projectTypeName?: string | null
  evaluationDeadline?: string | null
  evaluationStatus?: string | null
  isExpired?: boolean
  isSubmitted?: boolean
  evaluationForm?: any
  invitationResponseStatus?: string | null
  invitationResponseLabel?: string | null
  invitationRespondedAt?: string | null
  invitationResponseComment?: string | null
  isInvitationAccepted?: boolean
  isInvitationRejected?: boolean
}

type ReviewerAccessResponse = {
  data?: ReviewerAccessData
}

type CriterionKey = 'originality' | 'methodology' | 'workPackages' | 'expectedOutputs'
type ReviewerCriterionForm = {score: string; comment: string; details?: {[key: string]: string}}
type ReviewerForm = {
  originality: ReviewerCriterionForm
  methodology: ReviewerCriterionForm
  workPackages: ReviewerCriterionForm
  expectedOutputs: ReviewerCriterionForm
  durationOpinion: string
  durationComment: string
  budgetOpinion: string
  budgetComment: string
  additionalComment: string
}

type CriterionItem = {
  id: string
  text: string
  hint: string
}

type CriterionDefinition = {
  key: CriterionKey
  title: string
  max: number
  description: string
  attentionPoints: string[]
  items: CriterionItem[]
}

const scoreOptions = [
  {value: '5', label: '5 - Evet / Çok güçlü'},
  {value: '4', label: '4 - Büyük ölçüde uygun'},
  {value: '3', label: '3 - Kısmen uygun'},
  {value: '2', label: '2 - Zayıf'},
  {value: '1', label: '1 - Hayır / Yetersiz'},
]

const criteriaItems: CriterionDefinition[] = [
  {
    key: 'originality',
    title: 'Özgün Değer',
    max: 30,
    description:
      'Projenin literatürdeki konumu, araştırma sorusu/hipotezi, amaç-hedef uyumu ve beklenen bilimsel yenilik bu alanda birlikte değerlendirilir.',
    attentionPoints: [
      'Güncel literatürün yeterli ve doğru kullanılıp kullanılmadığını kontrol ediniz.',
      'Hipotezin açık, test edilebilir ve mevcut bilgi birikimine yenilik katacak nitelikte olmasına dikkat ediniz.',
      'Amaç, hedef ve hipotez arasında doğrudan ve tutarlı bir bağ aranmalıdır.',
    ],
    items: [
      {
        id: 'literaturePosition',
        text: 'Proje konusunun mevcut bilim ve teknolojideki yeri güncel literatür eşliğinde açık ve anlaşılır olarak tanımlanmıştır.',
        hint: 'Literatür boşluğu, güncel kaynaklar ve problemin bağlamı net mi?',
      },
      {
        id: 'questionHypothesis',
        text: 'Proje sorusu ve hipotezi açıkça tanımlanmıştır.',
        hint: 'Araştırma sorusu ve hipotez ölçülebilir/test edilebilir biçimde yazılmış mı?',
      },
      {
        id: 'goalAlignment',
        text: 'Amaç ve hedefler proje hipotezi ile uyumludur.',
        hint: 'Hedefler hipotezi sınamaya doğrudan hizmet ediyor mu?',
      },
      {
        id: 'hypothesisOriginality',
        text: 'Proje hipotezi özgündür.',
        hint: 'Hipotez tekrar çalışmadan öte yeni bilgi, yaklaşım veya bakış açısı sunuyor mu?',
      },
      {
        id: 'preliminarySupport',
        text: 'Proje hipotezi literatür ve/veya ön çalışmalar ile desteklenmiştir.',
        hint: 'Ön veri, pilot çalışma veya güçlü literatür dayanağı var mı?',
      },
      {
        id: 'innovationContribution',
        text: 'Proje önerisi kuram/bilgi/teknoloji olarak yenilik sağlamaktadır.',
        hint: 'Beklenen katkı bilimsel, teknolojik veya kuramsal olarak somutlaştırılmış mı?',
      },
    ],
  },
  {
    key: 'methodology',
    title: 'Gereç ve Yöntem',
    max: 20,
    description:
      'Yöntemlerin literatür dayanağı, hipotezi test etme gücü, kontrol kurgusu, örneklem ve istatistiksel analiz yeterliliği bu bölümde puanlanır.',
    attentionPoints: [
      'Yöntemlerin proje hedeflerini gerçekleştirmek için yeterli ayrıntıda açıklanmasına dikkat ediniz.',
      'Pozitif/negatif kontroller, örneklem seçimi, örneklem büyüklüğü ve analiz planı gerekçeli olmalıdır.',
      'Yöntemsel sınırlılıklar ve veri kalitesini güvenceye alan süreçler değerlendirilmelidir.',
    ],
    items: [
      {
        id: 'methodsLiterature',
        text: 'Yöntemler ilgili literatür eşliğinde belirgin ve doğru olarak açıklanmıştır.',
        hint: 'Protokol, ölçüm araçları ve veri toplama adımları anlaşılır mı?',
      },
      {
        id: 'designFit',
        text: 'Proje kurgusu ve seçilen yöntemler proje hipotezini test etmeye ve proje hedeflerine ulaşmaya uygundur.',
        hint: 'Araştırma tasarımı hipotezi sınamak için yeterli güce sahip mi?',
      },
      {
        id: 'controls',
        text: 'Yöntemsel kurgu verilerin değerlendirilmesi için gereken pozitif ve negatif kontrolleri içermektedir.',
        hint: 'Kontroller olası yanlılıkları ve hatalı yorumları azaltıyor mu?',
      },
      {
        id: 'sampleAnalysis',
        text: 'Örneklem seçimi, örneklem büyüklüğü ve istatistiksel analiz yöntemleri uygundur.',
        hint: 'Güç analizi, dahil/dışla kriterleri ve istatistik planı yeterli mi?',
      },
    ],
  },
  {
    key: 'workPackages',
    title: 'İş Paketleri ve Proje Yönetimi',
    max: 20,
    description:
      'İş paketlerinin planlanması, zaman çizelgesi, başarı ölçütleri, risk/B planları ve ekip yetkinliği bu alanda incelenir.',
    attentionPoints: [
      'İş paketleri hedeflerle ilişkilendirilmeli ve zaman çizelgesi gerçekçi olmalıdır.',
      'Başarı kriterleri ölçülebilir, izlenebilir ve proje çıktılarıyla bağlantılı olmalıdır.',
      'Riskler, B planları ve ekipteki uzmanlık dağılımı projenin yürütülebilirliğini desteklemelidir.',
    ],
    items: [
      {
        id: 'schedule',
        text: 'İş paketleri ve iş zaman çizelgesi uygundur.',
        hint: 'Sıralama, süreler, bağımlılıklar ve kilometre taşları gerçekçi mi?',
      },
      {
        id: 'successCriteria',
        text: 'Başarı kriterleri açık ve ölçülebilirdir.',
        hint: 'Başarı neye göre ölçülecek, eşik değerler veya göstergeler tanımlı mı?',
      },
      {
        id: 'riskPlans',
        text: 'Risk öngörüleri ve B planları yeterlidir ve uygundur.',
        hint: 'Teknik, idari, etik ve veri toplama risklerine uygulanabilir önlemler var mı?',
      },
      {
        id: 'teamExperience',
        text: 'Proje ekibinde gereken uzmanlık alanlarından ulusal ve/veya uluslararası deneyimi olan ekip üyesi vardır.',
        hint: 'Ekip yetkinliği iş paketlerini karşılıyor mu?',
      },
    ],
  },
  {
    key: 'expectedOutputs',
    title: 'Hedeflenen Proje Çıktıları ve Yaygın Etki',
    max: 30,
    description:
      'Projenin bilim/teknoloji katkısı, yayınlanabilirliği, yeni işbirliklerine temel oluşturması, toplumsal/ulusal faydası ve ticarileşme potansiyeli değerlendirilir.',
    attentionPoints: [
      'Beklenen çıktıların proje kapsamı, yöntem ve süre ile gerçekçi olup olmadığını değerlendiriniz.',
      'Yayın, kongre, yeni proje ve işbirliği potansiyeli somut göstergelerle desteklenmelidir.',
      'Toplumsal katkı, dışa bağımlılığı azaltma ve ticarileşme iddiaları gerekçeli olmalıdır.',
    ],
    items: [
      {
        id: 'scienceTechnologyContribution',
        text: 'Bilim ve teknolojiye katkı sağlama potansiyeli yüksektir.',
        hint: 'Katkı alanı, beklenen bilgi/teknoloji çıktısı ve etki mekanizması açık mı?',
      },
      {
        id: 'publicationPotential',
        text: 'Uluslararası hakemli, indekslere kayıtlı etki değeri yüksek dergilerde yayımlanabilir.',
        hint: 'Yöntem ve özgün değer nitelikli yayın üretmeye yeterli mi?',
      },
      {
        id: 'collaborationPotential',
        text: 'Ulusal/uluslararası kongrelerde sunulma, yeni projelere ve bilimsel işbirliklerine temel oluşturabilme özelliği vardır.',
        hint: 'Çıktılar yeni ağlar, proje fikirleri veya sunumlar için uygulanabilir mi?',
      },
      {
        id: 'socialProblemSolution',
        text: 'Toplumun sorunlarına çözüm üretebilme potansiyeli yüksektir.',
        hint: 'Toplumsal sorun, hedef kitle ve beklenen fayda net mi?',
      },
      {
        id: 'nationalDependency',
        text: 'Ülkemizin yurtdışına bağımlılığını gideren yeni bir ürün veya model geliştirme potansiyeli yüksektir.',
        hint: 'Yerelleşme, ürün/model geliştirme veya stratejik katkı gerçekçi mi?',
      },
      {
        id: 'commercialization',
        text: 'Sonuçların ticarileştirilme potansiyeli vardır.',
        hint: 'Patent, lisanslama, ürünleşme, girişim veya sektör kullanımına dair yol var mı?',
      },
    ],
  },
]

const API_URL = process.env.REACT_APP_API_URL
const BASE_URL = `${API_URL}/Custom`

const formatBapDateTime = (value?: string | null) => {
  if (!value) {
    return '-'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '-'
  }
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Istanbul',
  }).format(date)
}

const createEmptyDetails = (key: CriterionKey) =>
  criteriaItems
    .find((item) => item.key === key)
    ?.items.reduce((acc: {[key: string]: string}, item) => {
      acc[item.id] = ''
      return acc
    }, {}) || {}

const defaultForm: ReviewerForm = {
  originality: {score: '', comment: '', details: createEmptyDetails('originality')},
  methodology: {score: '', comment: '', details: createEmptyDetails('methodology')},
  workPackages: {score: '', comment: '', details: createEmptyDetails('workPackages')},
  expectedOutputs: {score: '', comment: '', details: createEmptyDetails('expectedOutputs')},
  durationOpinion: '',
  durationComment: '',
  budgetOpinion: '',
  budgetComment: '',
  additionalComment: '',
}

const BapReviewerPage: FC = () => {
  const [params] = useSearchParams()
  const token = useMemo(() => (params.get('token') || '').trim(), [params])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pdfPreparing, setPdfPreparing] = useState(false)
  const [pdfInfo, setPdfInfo] = useState('')
  const [reviewerData, setReviewerData] = useState<ReviewerAccessData | null>(null)
  const [invitationComment, setInvitationComment] = useState('')
  const [respondingInvitation, setRespondingInvitation] = useState<'accept' | 'reject' | null>(null)
  const [form, setForm] = useState<ReviewerForm>(defaultForm)

  const normalizeForm = (source?: any): ReviewerForm => {
    const getValue = (target: any, camelKey: string, pascalKey: string) =>
      target?.[camelKey] ?? target?.[pascalKey]
    const normalizeSection = (key: CriterionKey, target: any) => ({
      score: getValue(target, 'score', 'Score')?.toString?.() || '',
      comment: getValue(target, 'comment', 'Comment') || '',
      details: {
        ...createEmptyDetails(key),
        ...(getValue(target, 'details', 'Details') || {}),
      },
    })

    const normalized: ReviewerForm = {
      originality: normalizeSection('originality', source?.originality),
      methodology: normalizeSection('methodology', source?.methodology),
      workPackages: normalizeSection('workPackages', source?.workPackages),
      expectedOutputs: normalizeSection('expectedOutputs', source?.expectedOutputs),
      durationOpinion: getValue(source, 'durationOpinion', 'DurationOpinion') || '',
      durationComment: getValue(source, 'durationComment', 'DurationComment') || '',
      budgetOpinion: getValue(source, 'budgetOpinion', 'BudgetOpinion') || '',
      budgetComment: getValue(source, 'budgetComment', 'BudgetComment') || '',
      additionalComment: getValue(source, 'additionalComment', 'AdditionalComment') || '',
    }
    return normalized
  }

  const loadAccess = async () => {
    if (!token) {
      setError('Hakem erişim anahtarı bulunamadı.')
      return
    }

    try {
      setLoading(true)
      setError('')
      const response = await axios.post<ReviewerAccessResponse>(`${BASE_URL}/Bap/ReviewerAccess`, {
        token,
      })
      const data = response.data?.data || null
      setReviewerData(data)
      setForm(data?.isSubmitted ? defaultForm : normalizeForm(data?.evaluationForm))
    } catch (err: any) {
      setError(err?.response?.data || 'Hakem erişim bilgileri alınamadı.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAccess()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const calculateSectionScore = (key: CriterionKey, details?: {[key: string]: string}) => {
    const definition = criteriaItems.find((item) => item.key === key)
    if (!definition) return ''
    const values = definition.items.map((item) => Number(details?.[item.id] || 0))
    const total = values.reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0)
    return total > 0 ? String(total) : ''
  }

  const getTotalScore = () =>
    criteriaItems.reduce((sum, item) => sum + Number(form?.[item.key]?.score || 0), 0)

  const handleDetailScoreChange = (key: CriterionKey, detailKey: string, value: string) => {
    setForm((prev) => {
      const details = {
        ...(prev?.[key]?.details || createEmptyDetails(key)),
        [detailKey]: value,
      }
      return {
        ...prev,
        [key]: {
          ...(prev?.[key] || {}),
          details,
          score: calculateSectionScore(key, details),
        },
      }
    })
  }

  const handleCommentChange = (key: CriterionKey, value: string) => {
    setForm((prev) => ({
      ...prev,
      [key]: {
        ...(prev?.[key] || {}),
        comment: value,
      },
    }))
  }

  const validateForm = () => {
    const missingScoreSection = criteriaItems.find((item) =>
      item.items.some((criterion) => !form?.[item.key]?.details?.[criterion.id])
    )
    if (missingScoreSection) {
      return `${missingScoreSection.title} alanındaki tüm alt ölçütler için 1-5 arası puan seçiniz.`
    }

    const shortCommentSection = criteriaItems.find(
      (item) => (form?.[item.key]?.comment || '').trim().length < 300
    )
    if (shortCommentSection) {
      return `${shortCommentSection.title} gerekçesi en az 300 karakter olmalıdır.`
    }

    if (!form.durationOpinion) return 'Proje süresinin uygunluğuna ilişkin görüş seçiniz.'
    if (!form.budgetOpinion) return 'Proje bütçesinin uygunluğuna ilişkin görüş seçiniz.'
    return ''
  }

  const handleSaveDraft = async () => {
    if (!token) {
      setError('Hakem erişim anahtarı bulunamadı.')
      return
    }

    try {
      setSavingDraft(true)
      setError('')
      setSuccess('')
      await axios.post(`${BASE_URL}/Bap/ReviewerAccess/Draft`, {
        token,
        form,
      })
      setSuccess('Taslak kaydedildi.')
      await loadAccess()
    } catch (err: any) {
      setError(err?.response?.data || 'Taslak kaydedilemedi.')
    } finally {
      setSavingDraft(false)
    }
  }

  const handleSubmit = async () => {
    if (!token) {
      setError('Hakem erişim anahtarı bulunamadı.')
      return
    }

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    try {
      setSubmitting(true)
      setError('')
      setSuccess('')
      await axios.post(`${BASE_URL}/Bap/ReviewerAccess/Submit`, {
        token,
        form,
      })
      setSuccess('Değerlendirmeniz başarıyla kaydedildi.')
      await loadAccess()
    } catch (err: any) {
      setError(err?.response?.data || 'Değerlendirme gönderilirken bir hata oluştu.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleInvitationResponse = async (accepted: boolean) => {
    if (!token) {
      setError('Hakem erişim anahtarı bulunamadı.')
      return
    }

    try {
      setRespondingInvitation(accepted ? 'accept' : 'reject')
      setError('')
      setSuccess('')
      await axios.post(`${BASE_URL}/Bap/ReviewerAccess/InvitationResponse`, {
        token,
        accepted,
        comment: invitationComment.trim() || null,
      })
      setSuccess(accepted ? 'Hakem görevi kabul edildi.' : 'Hakem görevi reddedildi.')
      await loadAccess()
    } catch (err: any) {
      setError(err?.response?.data || 'Hakem davet yanıtı gönderilemedi.')
    } finally {
      setRespondingInvitation(null)
    }
  }

  const handleDownloadPdf = async () => {
    if (!token || pdfPreparing) return
    try {
      setPdfPreparing(true)
      setError('')
      setPdfInfo('Başvuru PDF dosyası hazırlanıyor. Lütfen bekleyiniz.')
      const response = await axios.get(
        `${BASE_URL}/Bap/Applications/${reviewerData?.applicationId}/Pdf`,
        {
          params: {reviewerToken: token},
          responseType: 'blob',
        }
      )
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], {type: 'application/pdf'})
      )
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', `bap-basvuru-${reviewerData?.applicationId || 'pdf'}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
      setPdfInfo('Başvuru PDF dosyası hazırlandı, indirme başlatıldı.')
    } catch (err: any) {
      setPdfInfo('')
      setError(err?.response?.data || 'Başvuru PDF dosyası indirilemedi.')
    } finally {
      setPdfPreparing(false)
    }
  }

  const isSubmitted = !!reviewerData?.isSubmitted
  const invitationResponseStatus = reviewerData?.invitationResponseStatus || 'Pending'
  const isInvitationAccepted =
    reviewerData?.isInvitationAccepted || invitationResponseStatus === 'Accepted'
  const isInvitationRejected =
    reviewerData?.isInvitationRejected || invitationResponseStatus === 'Rejected'
  const isWaitingInvitationResponse =
    !!reviewerData && !isSubmitted && !isInvitationAccepted && !isInvitationRejected
  const canFillEvaluation = !!reviewerData && isInvitationAccepted && !isInvitationRejected
  const isReadOnly = !!reviewerData?.isExpired || isSubmitted || !canFillEvaluation
  const totalScore = getTotalScore()

  return (
    <div className='container py-10'>
      <div className='card'>
        <div className='card-body'>
          <div className='d-flex flex-column flex-lg-row justify-content-between gap-4 mb-6'>
            <div>
              <h2 className='mb-2'>BAP Hakem Değerlendirme</h2>
              <div className='text-muted'>
                Alt ölçütleri 1-5 arasında puanlayın; bölüm puanları ve toplam puan otomatik
                hesaplanır.
              </div>
            </div>
            {!isSubmitted && canFillEvaluation && (
              <div className='border rounded p-4 bg-light min-w-200px'>
                <div className='text-muted fs-7'>Toplam Puan</div>
                <div className='fs-2 fw-bold'>{totalScore} / 100</div>
                <div className={totalScore >= 70 ? 'text-success fs-8' : 'text-muted fs-8'}>
                  Bilimsel başarı eşiği: toplam en az 70 puan
                </div>
              </div>
            )}
          </div>

          {loading && <div className='alert alert-info'>Yükleniyor...</div>}
          {error && <div className='alert alert-danger'>{error}</div>}
          {success && !isSubmitted && <div className='alert alert-success'>{success}</div>}
          {pdfInfo && !isSubmitted && <div className='alert alert-info'>{pdfInfo}</div>}

          {isSubmitted && (
            <div className='text-center py-10'>
              <div className='mb-5'>
                <i className='bi bi-check-circle-fill fs-3x text-success'></i>
              </div>
              <h3 className='mb-3'>Gönderim Tamamlandı</h3>
              <p className='text-muted mb-6'>
                Değerlendirmenizi tamamladığınız için teşekkür ederiz. Bu bağlantı üzerinden başvuru
                verilerine artık erişilemez.
              </p>
              <button className='btn btn-light-success' disabled>
                Gönderim Tamamlandı
              </button>
            </div>
          )}

          {reviewerData && !isSubmitted && (
            <>
              <div className='row g-4 mb-6'>
                <div className='col-lg-8'>
                  <div className='border rounded p-4 h-100'>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <strong>Hakem:</strong>{' '}
                        {reviewerData.reviewerName || reviewerData.reviewerEmail || '-'}
                      </div>
                      <div className='col-md-6'>
                        <strong>Proje Türü:</strong> {reviewerData.projectTypeName || '-'}
                      </div>
                      <div className='col-12'>
                        <strong>Başvuru:</strong> {reviewerData.applicationTitle || '-'}
                      </div>
                      <div className='col-md-6'>
                        <strong>Son Tarih:</strong>{' '}
                        {formatBapDateTime(reviewerData.evaluationDeadline)}
                      </div>
                      <div className='col-md-6'>
                        <strong>Durum:</strong> {reviewerData.evaluationStatus || '-'}
                      </div>
                      <div className='col-md-6'>
                        <strong>Davet Yanıtı:</strong>{' '}
                        {reviewerData.invitationResponseLabel ||
                          (isInvitationAccepted
                            ? 'Kabul Edildi'
                            : isInvitationRejected
                            ? 'Reddedildi'
                            : 'Yanıt Bekleniyor')}
                      </div>
                      {reviewerData.invitationRespondedAt && (
                        <div className='col-md-6'>
                          <strong>Yanıt Tarihi:</strong>{' '}
                          {formatBapDateTime(reviewerData.invitationRespondedAt)}
                        </div>
                      )}
                    </div>
                    <button
                      className='btn btn-light-primary btn-sm mt-4'
                      onClick={handleDownloadPdf}
                      disabled={pdfPreparing || isInvitationRejected}
                    >
                      {pdfPreparing && (
                        <span className='spinner-border spinner-border-sm me-2'></span>
                      )}
                      {pdfPreparing ? 'PDF hazırlanıyor...' : "Başvuru PDF'ini İndir"}
                    </button>
                  </div>
                </div>
                <div className='col-lg-4'>
                  <div className='alert alert-primary h-100 mb-0'>
                    <div className='fw-bold mb-2'>Puanlama Rehberi</div>
                    <div>
                      5: Evet/çok güçlü, 4: büyük ölçüde uygun, 3: kısmen uygun, 2: zayıf, 1:
                      hayır/yetersiz.
                    </div>
                    <div className='mt-2'>
                      Özgün değer için genel kural en az 20 puandır; LAİP, TEZP ve ADP türlerinde bu
                      eşik aranmaz.
                    </div>
                  </div>
                </div>
              </div>

              {isWaitingInvitationResponse && (
                <div className='border rounded p-5 mb-6'>
                  <div className='fw-bold fs-5 mb-2'>Hakemlik Görevi Yanıtı</div>
                  <div className='text-muted mb-4'>
                    Değerlendirmeye başlamadan önce bu hakemlik görevini kabul edip etmediğinizi
                    bildiriniz.
                  </div>
                  <textarea
                    className='form-control mb-4'
                    rows={3}
                    placeholder='Varsa açıklamanızı yazınız.'
                    value={invitationComment}
                    onChange={(e) => setInvitationComment(e.target.value)}
                  />
                  <div className='d-flex flex-column flex-sm-row gap-3'>
                    <button
                      className='btn btn-success'
                      disabled={!!respondingInvitation || !!reviewerData.isExpired}
                      onClick={() => handleInvitationResponse(true)}
                    >
                      {respondingInvitation === 'accept' ? 'Gönderiliyor...' : 'Görevi Kabul Et'}
                    </button>
                    <button
                      className='btn btn-light-danger'
                      disabled={!!respondingInvitation || !!reviewerData.isExpired}
                      onClick={() => handleInvitationResponse(false)}
                    >
                      {respondingInvitation === 'reject' ? 'Gönderiliyor...' : 'Görevi Reddet'}
                    </button>
                  </div>
                </div>
              )}

              {isInvitationRejected && (
                <div className='alert alert-danger'>
                  Hakemlik görevi reddedildi. Bu bağlantı üzerinden değerlendirme formu
                  gönderilemez.
                  {reviewerData.invitationResponseComment && (
                    <div className='mt-2'>Açıklama: {reviewerData.invitationResponseComment}</div>
                  )}
                </div>
              )}

              {canFillEvaluation && !reviewerData.isExpired && (
                <div className='alert alert-success'>
                  Hakemlik görevi kabul edildi. Değerlendirme formunu doldurabilirsiniz.
                </div>
              )}

              {isReadOnly && canFillEvaluation && (
                <div className='alert alert-warning'>
                  Bu değerlendirme formu düzenlemeye kapalıdır.
                </div>
              )}

              {canFillEvaluation &&
                criteriaItems.map((item, index) => {
                  const sectionScore = form?.[item.key]?.score || '0'
                  const commentLength = (form?.[item.key]?.comment || '').trim().length
                  return (
                    <div className='card border mb-6' key={item.key}>
                      <div className='card-header align-items-center'>
                        <div>
                          <h3 className='card-title mb-1'>
                            {index + 1}. {item.title}
                          </h3>
                          <div className='text-muted fs-7'>{item.description}</div>
                        </div>
                        <div className='badge badge-light-primary fs-6'>
                          {sectionScore} / {item.max}
                        </div>
                      </div>
                      <div className='card-body'>
                        <div className='notice d-flex bg-light-primary bg-opacity-25 rounded border-primary border border-dashed p-5 mb-6'>
                          <i className='bi bi-info-circle-fill text-primary fs-2 me-4 mt-1'></i>
                          <div className='d-flex flex-column'>
                            <div className='fw-bold text-gray-900 mb-2'>
                              Bu alanda nelere dikkat edilmeli?
                            </div>
                            <ul className='mb-0 ps-4 text-gray-800'>
                              {item.attentionPoints.map((point) => (
                                <li key={point}>{point}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className='table-responsive'>
                          <table className='table table-row-dashed align-middle'>
                            <thead>
                              <tr className='fw-bold text-muted'>
                                <th>Alt değerlendirme ölçütü</th>
                                <th className='w-250px'>Puan</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.items.map((criterion) => (
                                <tr key={criterion.id}>
                                  <td>
                                    <div className='fw-semibold'>{criterion.text}</div>
                                    <div className='text-muted fs-8'>{criterion.hint}</div>
                                  </td>
                                  <td>
                                    <select
                                      className='form-select form-select-sm'
                                      value={form?.[item.key]?.details?.[criterion.id] || ''}
                                      disabled={isReadOnly}
                                      onChange={(e) =>
                                        handleDetailScoreChange(
                                          item.key,
                                          criterion.id,
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option value=''>Puan seçiniz</option>
                                      {scoreOptions.map((option) => (
                                        <option
                                          key={`${criterion.id}-${option.value}`}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <label className='form-label fw-bold mt-3'>
                          Değerlendirmenizin gerekçeleri
                        </label>
                        <textarea
                          className='form-control'
                          rows={5}
                          placeholder='Bu bölüm için güçlü yönleri, eksiklikleri ve puan gerekçenizi en az 300 karakter olacak şekilde yazınız.'
                          value={form?.[item.key]?.comment ?? ''}
                          disabled={isReadOnly}
                          onChange={(e) => handleCommentChange(item.key, e.target.value)}
                        />
                        <div
                          className={
                            commentLength >= 300 ? 'text-success fs-8 mt-1' : 'text-muted fs-8 mt-1'
                          }
                        >
                          {commentLength} / 300 karakter
                        </div>
                      </div>
                    </div>
                  )
                })}

              {canFillEvaluation && (
                <>
                  <div className='card border mb-6'>
                    <div className='card-header'>
                      <h3 className='card-title'>Süre, Bütçe ve Diğer Görüşler</h3>
                    </div>
                    <div className='card-body'>
                      <div className='row g-4'>
                        <div className='col-lg-6'>
                          <label className='form-label fw-bold'>
                            Proje Süresinin Uygunluğuna İlişkin Görüş
                          </label>
                          <select
                            className='form-select mb-2'
                            value={form.durationOpinion || ''}
                            disabled={isReadOnly}
                            onChange={(e) =>
                              setForm((prev) => ({...prev, durationOpinion: e.target.value}))
                            }
                          >
                            <option value=''>Seçiniz</option>
                            <option value='yeterli'>Yeterli</option>
                            <option value='çok kısa'>Çok kısa</option>
                            <option value='çok uzun'>Çok uzun</option>
                          </select>
                          <textarea
                            className='form-control'
                            rows={4}
                            placeholder='Sürenin iş paketleri, veri toplama ve analiz aşamaları için neden yeterli/yetersiz olduğunu açıklayınız.'
                            value={form.durationComment || ''}
                            disabled={isReadOnly}
                            onChange={(e) =>
                              setForm((prev) => ({...prev, durationComment: e.target.value}))
                            }
                          />
                        </div>
                        <div className='col-lg-6'>
                          <label className='form-label fw-bold'>
                            Proje Bütçesinin Uygunluğuna İlişkin Görüş
                          </label>
                          <select
                            className='form-select mb-2'
                            value={form.budgetOpinion || ''}
                            disabled={isReadOnly}
                            onChange={(e) =>
                              setForm((prev) => ({...prev, budgetOpinion: e.target.value}))
                            }
                          >
                            <option value=''>Seçiniz</option>
                            <option value='yeterli'>Yeterli</option>
                            <option value='yetersiz'>Yetersiz</option>
                            <option value='fazla'>Fazla</option>
                          </select>
                          <textarea
                            className='form-control'
                            rows={4}
                            placeholder='Bütçe kalemlerinin yöntem, iş paketleri ve beklenen çıktılar ile uyumunu açıklayınız.'
                            value={form.budgetComment || ''}
                            disabled={isReadOnly}
                            onChange={(e) =>
                              setForm((prev) => ({...prev, budgetComment: e.target.value}))
                            }
                          />
                        </div>
                        <div className='col-12'>
                          <label className='form-label fw-bold'>
                            Proje ile İlgili Diğer Görüşler
                          </label>
                          <textarea
                            className='form-control'
                            rows={4}
                            placeholder='Varsa etik, yürütülebilirlik, raporlama veya proje yürütücüsüne iletilmesini istediğiniz ek önerileri yazınız.'
                            value={form.additionalComment || ''}
                            disabled={isReadOnly}
                            onChange={(e) =>
                              setForm((prev) => ({...prev, additionalComment: e.target.value}))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'>
                    <div className='text-muted'>
                      Göndermeden önce tüm alt ölçütleri puanladığınızdan ve bölüm gerekçelerini
                      tamamladığınızdan emin olunuz.
                    </div>
                    {!isReadOnly && (
                      <div className='d-flex flex-column flex-sm-row gap-2'>
                        <button
                          className='btn btn-light-primary'
                          disabled={savingDraft || submitting}
                          onClick={handleSaveDraft}
                        >
                          {savingDraft ? 'Kaydediliyor...' : 'Taslak Kaydet'}
                        </button>
                        <button
                          className='btn btn-primary'
                          disabled={submitting || savingDraft}
                          onClick={handleSubmit}
                        >
                          {submitting ? 'Gönderiliyor...' : 'Değerlendirmeyi Gönder'}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default BapReviewerPage
